import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { Dispatch } from 'react';
import { useCallback } from 'react';

import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { errorCodes } from '@sorare/core/src/hooks/graphql/useMutation';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';

import { useCreateDirectOffer } from '#src/hooks/offers/useCreateDirectOffer.ts';

import type { useOnSubmit_anyCard } from './__generated__/useOnSubmit.graphql.ts';
import { switchToBuilding, switchToSubmitting } from './actions.ts';
import type { Actions, State } from './types.ts';

export const useOnSubmit = <D extends useOnSubmit_anyCard>(
  to: { slug: string },
  onDone: () => void,
  counteredOfferId?: string
) => {
  const createOffer = useCreateDirectOffer();
  const { canListAndTrade } = useFiatBalance();
  const { cryptocurrency } = useCurrentUserCurrencies();
  const { showNotification } = useSnackNotificationContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  return useCallback(
    async (dispatch: Dispatch<Actions<D>>, state: State<D>) => {
      dispatch(switchToSubmitting);
      try {
        const {
          sendCards,
          receiveCards,
          cardsData,
          sendAmount: sendMonetaryAmount,
          receiveAmount: receiveMonetaryAmount,
          sendAmountCurrency: stateSendAmountCurrency,
          receiveAmountCurrency: stateReceiveAmountCurrency,
        } = state;

        const sendAmountCurrency = canListAndTrade
          ? stateSendAmountCurrency
          : cryptocurrency;
        const receiveAmountCurrency = canListAndTrade
          ? stateReceiveAmountCurrency
          : cryptocurrency;

        if (![...sendCards, ...receiveCards].every(c => cardsData[c.slug]))
          throw new Error('Please retry later [Missing Card data]');

        const errors = await createOffer({
          receiverSlug: to.slug,
          sendMonetaryAmount: toMonetaryAmount(sendMonetaryAmount),
          receiveMonetaryAmount: toMonetaryAmount(receiveMonetaryAmount),
          sendTokens: sendCards.map(c => cardsData[c.slug]),
          receiveTokens: receiveCards.map(c => cardsData[c.slug]),
          duration: state.duration * 24 * 60 * 60,
          counteredOfferId,
          sendAmountCurrency,
          receiveAmountCurrency,
        });
        if (errors) {
          if (
            errors.some(
              error =>
                typeof error === 'object' &&
                'code' in error &&
                error?.code === errorCodes.unverifiedEmail
            )
          ) {
            onDone();
          }
          dispatch(switchToBuilding);
        } else {
          onDone();
          showNotification('offerSent');
        }
      } catch (e) {
        if (e && typeof e === 'object' && 'message' in e) {
          showNotification('errors', { errors: e.message });
        }
        dispatch(switchToBuilding);
      }
    },
    [
      canListAndTrade,
      createOffer,
      to.slug,
      counteredOfferId,
      onDone,
      showNotification,
      toMonetaryAmount,
      cryptocurrency,
    ]
  );
};

useOnSubmit.fragments = {
  anyCard: gql`
    fragment useOnSubmit_anyCard on AnyCardInterface {
      slug
      ...useCreateDirectOffer_anyCard
    }
    ${useCreateDirectOffer.fragments.anyCard}
  ` as TypedDocumentNode<useOnSubmit_anyCard>,
};
